<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import Icon from "./Icon.vue";
import { api, type AssistState, type PendingAction, type TranscriptItem } from "../api";

// Czat z asystentem AI przy naprawie. Asystent sam czyta pliki i sprawdza
// stronę; każdą ZMIANĘ pokazuje jako kartę do zatwierdzenia — nic nie wykona
// się na serwerze bez kliknięcia „Zastosuj".

const props = defineProps<{
  runId: string;
  isAdmin: boolean;
  domain: string;
  /**
   * Opis problemu z ankiety „czy strona działa?" — wysyłany jako pierwsza
   * wiadomość, żeby klient nie pisał tego samego drugi raz. Rodzic zeruje po
   * `seeded`, więc odświeżenie strony go nie powtórzy.
   */
  seed?: string | null;
}>();
const emit = defineEmits<{ escalate: []; seeded: [] }>();

const state = ref<AssistState | null>(null);
const text = ref("");
/** Trwa nasze żądanie HTTP (krótkie: serwer odpowiada od razu i pracuje w tle). */
const busy = ref(false);
const err = ref("");
const box = ref<HTMLElement | null>(null);

// Asystent pracuje po stronie serwera W TLE; stan `busy` przychodzi z API.
// Dopóki jest ustawiony, odpytujemy co 2,5 s — transkrypt rośnie na żywo,
// a odświeżenie strony niczego nie psuje (stan jest w bazie, nie w żądaniu).
const working = computed(() => busy.value || !!state.value?.busy);
let pollTimer: ReturnType<typeof setTimeout> | null = null;
function schedulePoll() {
  if (pollTimer) clearTimeout(pollTimer);
  if (!state.value?.busy) return;
  pollTimer = setTimeout(async () => {
    pollTimer = null;
    try {
      state.value = await api.get<AssistState>(base.value);
      await scrollDown();
    } catch (e) {
      err.value = (e as Error).message;
    }
    schedulePoll();
  }, 2500);
}
onUnmounted(() => { if (pollTimer) clearTimeout(pollTimer); });

const base = computed(() => `/api/runs/${encodeURIComponent(props.runId)}/assist`);

async function scrollDown() {
  await nextTick();
  if (box.value) box.value.scrollTop = box.value.scrollHeight;
}

async function load() {
  err.value = "";
  try {
    state.value = await api.get<AssistState>(base.value);
    await scrollDown();
    schedulePoll();
  } catch (e) {
    err.value = (e as Error).message;
  }
}
onMounted(load);
watch(() => props.runId, load);
watch(
  () => props.seed,
  async (v) => {
    if (!v) return;
    if (!state.value) await load();
    emit("seeded");
    await send(v);
  },
  { immediate: true },
);

const SUGGESTIONS = [
  "Strona ładuje się bez styli (CSS).",
  "Strona pokazuje błąd 500 / białą stronę.",
  "Nie działa formularz kontaktowy.",
  "Nie mogę zalogować się do panelu administracyjnego strony.",
];

async function send(msg?: string) {
  const t = (msg ?? text.value).trim();
  if (!t || working.value) return;
  err.value = "";
  busy.value = true;
  // Optymistycznie pokazujemy wiadomość, zanim wróci odpowiedź (może potrwać minutę).
  state.value?.messages.push({ id: -1, role: "user", author: null, createdAt: new Date().toISOString(), parts: [{ kind: "text", text: t }] });
  text.value = "";
  await scrollDown();
  try {
    state.value = await api.post<AssistState>(`${base.value}/messages`, { text: t });
    await scrollDown();
    schedulePoll();
  } catch (e) {
    err.value = (e as Error).message;
    await load();
  } finally {
    busy.value = false;
  }
}

async function decide(a: PendingAction, approve: boolean) {
  if (working.value) return;
  err.value = "";
  busy.value = true;
  try {
    state.value = await api.post<AssistState>(`${base.value}/actions/${encodeURIComponent(a.id)}`, { approve });
    await scrollDown();
    schedulePoll();
  } catch (e) {
    err.value = (e as Error).message;
    await load();
  } finally {
    busy.value = false;
  }
}

const open = ref<Record<string, boolean>>({});
const resultFor = (m: TranscriptItem[], id: string) => {
  for (const msg of m) for (const p of msg.parts) if (p.kind === "result" && p.toolUseId === id) return p;
  return null;
};
const visible = computed(() => (state.value?.messages ?? []).filter((m) => m.parts.some((p) => p.kind !== "result")));
const isSystem = (m: TranscriptItem) => m.role === "user" && m.parts.some((p) => p.kind === "text" && p.text.startsWith("[system]"));
const sysText = (t: string) => t.replace(/^\[system\]\s*/, "");
const hasConversation = computed(() => visible.value.length > 0);
</script>

<template>
  <div class="chat">
    <div class="intro" v-if="!hasConversation">
      <p><b>Asystent AI</b> ma dostęp do plików i logów Twojej strony {{ domain }}. Opisz, co nie działa, a sprawdzi przyczynę i zaproponuje poprawkę. Każdą zmianę na serwerze zatwierdzasz jednym kliknięciem — nic nie dzieje się bez Twojej zgody.</p>
      <div class="sugg">
        <button v-for="s in SUGGESTIONS" :key="s" type="button" class="sec small" :disabled="working" @click="send(s)">{{ s }}</button>
      </div>
    </div>

    <div ref="box" class="msgs" :class="{ empty: !hasConversation }">
      <div v-for="m in visible" :key="m.id" class="msg" :class="isSystem(m) ? 'system' : m.role">
        <div class="who">{{ isSystem(m) ? "Panel" : m.role === "user" ? (m.author ?? "Ty") : "Asystent" }}</div>
        <template v-for="(p, i) in m.parts" :key="i">
          <p v-if="p.kind === 'text'" class="txt">{{ isSystem(m) ? sysText(p.text) : p.text }}</p>
          <div v-else-if="p.kind === 'tool'" class="tool">
            <button type="button" class="link" @click="open[p.toolUseId] = !open[p.toolUseId]">
              <Icon name="wrench" :size="14" /> {{ p.summary }} <Icon :name="open[p.toolUseId] ? 'chevron-up' : 'chevron-down'" :size="14" />
            </button>
            <template v-if="open[p.toolUseId]">
              <pre v-if="p.name === 'write_file' && typeof p.input.content === 'string'" class="out">{{ p.input.content }}</pre>
              <pre v-if="resultFor(state!.messages, p.toolUseId)" class="out" :class="{ bad: resultFor(state!.messages, p.toolUseId)!.isError }">{{ resultFor(state!.messages, p.toolUseId)!.text }}</pre>
            </template>
          </div>
        </template>
      </div>
      <div v-if="working" class="msg assistant"><div class="who">Asystent</div><p class="txt muted"><span class="dots" /> analizuję… (możesz odświeżyć stronę, praca trwa na serwerze)</p></div>
    </div>

    <!-- Akcja do zatwierdzenia -->
    <div v-for="a in state?.pendingActions ?? []" :key="a.id" class="approve">
      <div class="ah"><Icon name="alert" /> Asystent prosi o zgodę na zmianę na serwerze</div>
      <p v-if="a.input.reason" class="why">{{ a.input.reason }}</p>
      <template v-if="a.tool === 'run_command'">
        <div class="lbl">Komenda do wykonania:</div>
        <pre class="cmd">{{ a.input.command }}</pre>
      </template>
      <template v-else>
        <div class="lbl">Zapis pliku <code>{{ a.input.path }}</code> (poprzednia wersja zostanie zachowana):</div>
        <pre class="cmd">{{ a.input.content }}</pre>
      </template>
      <div class="row">
        <button type="button" :disabled="working" @click="decide(a, true)"><Icon name="check" /> Zastosuj</button>
        <button type="button" class="sec" :disabled="working" @click="decide(a, false)"><Icon name="x" /> Odrzuć</button>
      </div>
    </div>

    <p v-if="err" class="err">{{ err }}</p>

    <form class="input" @submit.prevent="send()">
      <input v-model="text" type="text" :disabled="working || (state?.pendingActions.length ?? 0) > 0" :placeholder="working ? 'Asystent pracuje…' : (state?.pendingActions.length ?? 0) > 0 ? 'Najpierw zatwierdź albo odrzuć akcję powyżej' : 'Opisz, co nie działa…'" maxlength="8000">
      <button type="submit" :disabled="working || !text.trim() || (state?.pendingActions.length ?? 0) > 0">Wyślij</button>
    </form>

    <div class="foot">
      <span v-if="state?.session.usage.calls" class="muted small">{{ state.session.usage.calls }} odpowiedzi{{ isAdmin ? ` • ~$${state.session.usage.costUsd.toFixed(2)}` : "" }}</span>
      <span class="spacer" />
      <button v-if="!isAdmin" type="button" class="link" @click="emit('escalate')">Asystent nie pomógł? Poproś o pomoc devopsa <Icon name="chevron-right" /></button>
    </div>
  </div>
</template>

<style scoped>
.chat { display: flex; flex-direction: column; gap: var(--s3); }
.intro p { font-size: 13px; color: var(--mut); margin: 0 0 var(--s2); line-height: 1.55; }
.intro b { color: var(--fg); }
.sugg { display: flex; flex-wrap: wrap; gap: 6px; }
.sugg button { white-space: normal; text-align: left; }
.msgs { display: flex; flex-direction: column; gap: var(--s3); max-height: 520px; overflow-y: auto; padding: 2px; }
.msgs.empty { display: none; }
.msg { max-width: 92%; }
.msg.user { align-self: flex-end; }
.msg.user .txt { background: var(--acc); color: #fff; border-radius: 14px 14px 4px 14px; }
.msg.assistant .txt { background: var(--panel2); border: 1px solid var(--bd); border-radius: 14px 14px 14px 4px; }
.msg.system { align-self: center; max-width: 100%; }
.msg.system .txt { background: var(--warn-dim); border: 1px solid var(--warn); border-radius: var(--r1); font-size: 12px; }
.msg.system .who { text-align: center; }
.who { font-size: 10px; color: var(--mut2); text-transform: uppercase; letter-spacing: .05em; margin: 0 6px 3px; }
.msg.user .who { text-align: right; }
.txt { margin: 0 0 4px; padding: 9px 13px; font-size: 13px; line-height: 1.55; white-space: pre-wrap; word-break: break-word; }
.tool { margin: 2px 6px; font-size: 12px; }
.tool .link { color: var(--mut); font-size: 12px; }
.out { background: var(--logbg); color: #c9d6e6; border: 1px solid var(--bd); border-radius: var(--r1); padding: var(--s2); margin: 4px 0; max-height: 220px; overflow: auto; font: 11px/1.5 var(--mono); white-space: pre-wrap; }
.out.bad { border-color: var(--crit); }
.dots::after { content: "●●●"; letter-spacing: 2px; animation: blink 1.2s infinite; }
@keyframes blink { 0%, 100% { opacity: .3; } 50% { opacity: 1; } }
.approve { border: 1px solid var(--warn); background: var(--warn-dim); border-radius: var(--r2); padding: var(--s3) var(--s4); }
.ah { font-weight: 650; font-size: 13px; margin-bottom: var(--s2); }
.why { font-size: 13px; margin: 0 0 var(--s2); }
.lbl { font-size: 12px; color: var(--mut); margin-bottom: 4px; }
.cmd { background: var(--logbg); color: #c9d6e6; border-radius: var(--r1); padding: var(--s2) var(--s3); font: 12px/1.5 var(--mono); white-space: pre-wrap; max-height: 260px; overflow: auto; margin: 0 0 var(--s3); }
.input { display: flex; gap: var(--s2); }
.input input { flex: 1; }
.foot { display: flex; align-items: center; gap: var(--s2); }
.small { font-size: 11px; }
</style>
